"use client"

import type React from "react"
import { useState } from "react"
import Link from "next/link"
import { AuthShell, SubmitBtn, AltLink, MailIcon } from "@/components/auth-shell"

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("")
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setLoading(true)

    try {
      const response = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      })

      const data = await response.json()

      if (data.success) {
        setSuccess(true)
      } else {
        setError(data.message || "Failed to send reset email")
      }
    } catch (err) {
      setError("An error occurred. Please try again.")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <AuthShell tag="passwd --reset">
        <div style={{ textAlign: "center" }}>
          {/* Success icon */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 48,
              height: 48,
              margin: "0 auto 20px",
              borderRadius: "50%",
              background: "var(--success)",
              opacity: 0.15,
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--success)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ position: "relative", zIndex: 1 }}
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          <h1
            style={{
              fontSize: 26,
              fontWeight: 600,
              letterSpacing: "-0.025em",
              color: "var(--text)",
              margin: 0,
            }}
          >
            Check your email
          </h1>
          <p style={{ fontSize: 14, color: "var(--text-2)", marginTop: 10 }}>
            We&apos;ve sent password reset instructions to{" "}
            <span style={{ color: "var(--text)", fontWeight: 500 }}>{email}</span>
          </p>

          <div
            style={{
              marginTop: 20,
              padding: "12px 14px",
              borderRadius: 6,
              border: "1px solid var(--accent-line)",
              background: "var(--accent-soft)",
              fontSize: 13,
              color: "var(--text-2)",
            }}
          >
            The reset link will expire in 1 hour. Check your spam folder if you don&apos;t see the
            email.
          </div>
        </div>

        <AltLink>
          <Link
            href="/login"
            className="font-mono"
            style={{
              color: "var(--accent)",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              fontSize: 13,
            }}
          >
            ← Back to login
          </Link>
        </AltLink>
      </AuthShell>
    )
  }

  return (
    <AuthShell tag="passwd --reset">
      <div style={{ textAlign: "center", marginBottom: 26 }}>
        <h1
          style={{
            fontSize: 26,
            fontWeight: 600,
            letterSpacing: "-0.025em",
            color: "var(--text)",
            margin: 0,
          }}
        >
          Forgot password?
        </h1>
        <p style={{ fontSize: 14, color: "var(--text-2)", marginTop: 8 }}>
          No worries, we&apos;ll send you reset instructions
        </p>
      </div>

      {error && (
        <div
          style={{
            marginBottom: 20,
            padding: "12px 14px",
            borderRadius: 6,
            border: "1px solid oklch(0.5 0.15 25 / 0.3)",
            background: "oklch(0.5 0.15 25 / 0.1)",
            fontSize: 13,
            color: "oklch(0.7 0.15 25)",
          }}
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Email field */}
          <div>
            <label
              style={{
                display: "block",
                fontSize: 13,
                fontWeight: 500,
                color: "var(--text-2)",
                marginBottom: 8,
              }}
            >
              Email
            </label>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                height: 46,
                background: "var(--bg)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                overflow: "hidden",
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 46,
                  color: "var(--muted)",
                }}
              >
                <MailIcon />
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                style={{
                  flex: 1,
                  height: "100%",
                  border: "none",
                  background: "transparent",
                  color: "var(--text)",
                  fontSize: 14,
                  outline: "none",
                  paddingRight: 14,
                }}
              />
            </div>
          </div>

          <SubmitBtn loading={loading} loadingText="Sending...">
            Send reset instructions
          </SubmitBtn>
        </div>
      </form>

      <AltLink>
        <Link
          href="/login"
          className="font-mono"
          style={{
            color: "var(--accent)",
            textDecoration: "none",
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            fontSize: 13,
          }}
        >
          ← Back to login
        </Link>
      </AltLink>
    </AuthShell>
  )
}
